/**
 * Tutor SignalR 推送（对齐 Vue2 utils/signalR.js）
 * - 启用时连 teacherHub，注册 JoinUser + 各业务事件
 * - 关闭时走 HTTP 轮询兜底（与现网一致）
 */
import * as signalR from "@microsoft/signalr";
import { getTutorSysMsg, getTutorClassMsgList } from "@/api/layout";
import { NotReadMsgCount, getTutorMoodleMsgList } from "@/api/message";
import { useSystemStore } from "@/stores/system";
import { useUserStore } from "@/stores/user";
import { useMessageStore } from "@/stores/message";
import { useClassDetailStore } from "@/stores/classDetail";

const signalrNotify = {
  hubConnection: null,
  hubUrl: "http://localhost:7008/teacherHub",
  tryCount: 10,
  retryCount: 0,
  currentUserId: 0,
  monitorTime: -1,
  functionCallBack: [],
  isShowNotifyCallBackLog: false,
  isShowConnectLog: false,
  isLocationDebug: location.hostname === "localhost",
  closeByCustom: false,
  timer: 60 * 1000,
  isEnableSignalrNotice: false,
  _started: false,
  _listenersBound: false,

  init() {
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(this.hubUrl)
      .configureLogging(signalR.LogLevel.Error)
      .build();
  },

  isIE() {
    const UA = navigator.userAgent;
    if (/msie/i.test(UA)) return true;
    if (UA.toLowerCase().indexOf("trident") > -1 && UA.indexOf("rv") > -1) return true;
    return false;
  },

  start() {
    const then = this;
    const systemStore = useSystemStore();
    const userStore = useUserStore();

    then.isEnableSignalrNotice = !!systemStore.isEnableSignalrNotice;

    // 未开 SignalR：只跑 HTTP 轮询注册
    if (!then.isEnableSignalrNotice) {
      then.RegisterAll();
      then._started = true;
      return;
    }

    if (!userStore.tuid || userStore.tuid <= 0) {
      console.log(`userId(${userStore.tuid})不合法`);
      return;
    }

    // 已连接且同一用户：不重复 start
    if (
      then._started &&
      then.hubConnection &&
      then.hubConnection.state !== signalR.HubConnectionState.Disconnected &&
      then.currentUserId === userStore.tuid
    ) {
      return;
    }

    if (systemStore.signalrNoticeHubUrl) {
      then.hubUrl = systemStore.signalrNoticeHubUrl;
    }

    then.init();
    then.currentUserId = userStore.tuid;
    then.closeByCustom = false;
    then.retryCount = 0;

    then.RegisterAll();
    then.connect();
    then._started = true;

    if (!then._listenersBound) {
      then._listenersBound = true;
      window.addEventListener("beforeunload", () => {
        then.close();
        if (then.isIE()) return "";
        return null;
      });
      window.addEventListener("storage", (ev) => {
        if (!ev.newValue) return;
        if (
          ev.key === "signalrAllClose" &&
          then.hubConnection?.state === signalR.HubConnectionState.Connected
        ) {
          signalrNotify.closeByCustom = true;
          signalrNotify.close();
        }
      });
    }

    then.hubConnection.onclose(() => {
      then.isShowConnectLog &&
        console.log(
          "disconnected--》连接断开--" +
            (then.closeByCustom ? "其他上线 通知关闭切换" : "系统断开")
        );
      try {
        then.hubConnection.off("JoinUserNotify");
      } catch (_) {
        /* ignore */
      }

      if (!then.closeByCustom) {
        setTimeout(() => {
          console.warn("disconnected--》断开--重连");
          then.connect();
        }, 5000);
      }
    });
  },

  connect() {
    const then = this;
    if (!then.hubConnection) return;

    if (then.hubConnection.state !== signalR.HubConnectionState.Disconnected) {
      then.isShowConnectLog && console.log("connected--未处于断开状态");
      return;
    }

    const connectSignalR = () => {
      then.hubConnection
        .start()
        .then(() => {
          then.isShowConnectLog && console.log("connected--》连接成功" + then.hubUrl);
          localStorage.setItem("signalrLastConnectTime", String(Date.now()));
          then.joinUser.register();
          then.joinUser.join(then.currentUserId);
        })
        .catch((err) => {
          console.warn("connecteFall--》连接失败", err);
          if (then.retryCount >= then.tryCount) {
            console.error("已超过最大重试次数" + then.tryCount + "不再重试");
            return;
          }
          setTimeout(() => {
            then.retryCount++;
            console.warn("connecteFall--》连接失败--重连");
            then.connect();
          }, 5000);
        });
    };

    setTimeout(() => {
      if (then.hubConnection.state !== signalR.HubConnectionState.Disconnected) {
        then.isShowConnectLog && console.log("connected--未处于断开状态");
        return;
      }
      connectSignalR();
    }, 1000);
  },

  close() {
    const then = this;
    if (!then.hubConnection) return;
    if (then.hubConnection.state === signalR.HubConnectionState.Disconnected) return;
    then.hubConnection.stop().catch(() => {});
    try {
      then.hubConnection.off("JoinUserNotify");
    } catch (_) {
      /* ignore */
    }
  },

  stop() {
    this.closeByCustom = true;
    if (this.monitorTime && this.monitorTime !== -1) {
      clearInterval(this.monitorTime);
      this.monitorTime = -1;
    }
    this.close();
    this._started = false;
    this.functionCallBack = [];
  },

  monitor() {
    const then = this;
    if (then.monitorTime && then.monitorTime !== -1) {
      clearInterval(then.monitorTime);
    }
    if (then.retryCount >= then.tryCount) {
      console.error("已超过最大重试次数" + then.tryCount + "不再监听");
      return;
    }
    then.monitorTime = setInterval(() => {
      if (
        !then.closeByCustom &&
        then.hubConnection?.state === signalR.HubConnectionState.Disconnected
      ) {
        if (then.retryCount >= then.tryCount) {
          clearInterval(then.monitorTime);
          console.error("已超过最大重试次数" + then.tryCount + "不再监听");
          return;
        }
        then.connect();
      }
    }, 60 * 1000);
  },

  pushFunctionCallBack(func, callBack, eventKey, funcName) {
    const exists = this.functionCallBack.some((a) => a.funcName === funcName);
    if (!exists) {
      this.functionCallBack.push({ func, callBack, eventKey, funcName });
    }
  },

  RegisterAll() {
    const then = this;
    const messageStore = useMessageStore();

    then.getTutorMoodleMsgListNotifyRegister((data) => {
      messageStore.addMoodleMsg(data);
    });

    then.getTutorClassMsgListNotifyRegister((data) => {
      messageStore.addClassMsg(data);
    });

    then.getTutorClassRemarkRemindNotifyRegister((data) => {
      messageStore.addSysMsg({ ...data, remind: true });
    });

    then.getTutorSysMsgListNotifyRegister((data) => {
      messageStore.addSysMsg(data);
    });

    then.teacheIsPaypalModifyNotifyRegister((data) => {
      messageStore.addPaypalMsg(data);
    });

    then.getNotReadMsgCountNotifyRegister((data) => {
      messageStore.addNoReadMsg(data);
      // 细分角标再拉一次
      messageStore.fetchUnreadTotals();
    });

    then.endClassNoticeRegister((clid) => {
      useClassDetailStore().fetchInfo(clid);
    });

    then.getTutorSelfScheduleRemindRegister((data) => {
      messageStore.addSelfScheduleRemindMsg(data);
    });

    then.getTutorAttendanceWarning((data) => {
      const row = Array.isArray(data) ? data[0] : data;
      if (row) messageStore.addAttendanceWarning(row);
    });
  },

  joinUser: {
    register() {
      const then = signalrNotify;
      then.hubConnection.on("JoinTeacherNotify", (data) => {
        then.isLocationDebug && console.log("JoinTeacherNotify", data);
        then.retryCount = 0;
        then.monitor();
        then.isShowConnectLog && console.log(data);
      });
    },
    join(userId) {
      const then = signalrNotify;
      if (then.hubConnection.state === signalR.HubConnectionState.Disconnected) {
        then.isShowConnectLog && console.log("connected--处于断开状态，无法join");
        return;
      }
      then.isLocationDebug && console.log("userId", userId);
      then.hubConnection.invoke("JoinUser", userId).catch(() => {
        then.isShowConnectLog && console.log("处于断开状态，无法join");
      });
    },
  },

  getTutorMoodleMsgListNotifyRegister(callBack) {
    const then = signalrNotify;
    if (!then.isEnableSignalrNotice) {
      getTutorMoodleMsgList()
        .then((res) => {
          if (res.data?.ErrorCode !== 0) {
            setTimeout(() => then.getTutorMoodleMsgListNotifyRegister(callBack), then.timer);
            return;
          }
          if (typeof callBack === "function") callBack(res.data.Body);
        })
        .catch(() => {
          setTimeout(() => then.getTutorMoodleMsgListNotifyRegister(callBack), then.timer);
        });
      return;
    }
    then.pushFunctionCallBack(
      then.getTutorMoodleMsgListNotifyRegister,
      callBack,
      "GetTutorMoodleMsgList",
      "getTutorMoodleMsgListNotifyRegister"
    );
    then.hubConnection.on("GetTutorMoodleMsgList", (data) => {
      then.isLocationDebug && console.log("GetTutorMoodleMsgList", data);
      if (typeof callBack === "function") callBack(data);
    });
  },

  getTutorClassMsgListNotifyRegister(callBack) {
    const then = signalrNotify;
    if (!then.isEnableSignalrNotice) {
      getTutorClassMsgList()
        .then((res) => {
          if (res.data?.ErrorCode !== 0) {
            setTimeout(() => then.getTutorClassMsgListNotifyRegister(callBack), then.timer);
            return;
          }
          if (typeof callBack === "function") callBack(res.data.Body);
        })
        .catch(() => {
          setTimeout(() => then.getTutorClassMsgListNotifyRegister(callBack), then.timer);
        });
      return;
    }
    then.pushFunctionCallBack(
      then.getTutorClassMsgListNotifyRegister,
      callBack,
      "GetTutorClassMsgList",
      "getTutorClassMsgListNotifyRegister"
    );
    then.hubConnection.on("GetTutorClassMsgList", (data) => {
      then.isLocationDebug && console.log("GetTutorClassMsgList", data);
      if (typeof callBack === "function") callBack(data);
    });
  },

  getTutorClassRemarkRemindNotifyRegister(callBack) {
    const then = signalrNotify;
    if (!then.isEnableSignalrNotice) return;
    then.pushFunctionCallBack(
      then.getTutorClassRemarkRemindNotifyRegister,
      callBack,
      "GetTutorClassRemarkRemind",
      "getTutorClassRemarkRemindNotifyRegister"
    );
    then.hubConnection.on("GetTutorClassRemarkRemind", (data) => {
      then.isLocationDebug && console.log("GetTutorClassRemarkRemind", data);
      if (typeof callBack === "function") callBack(data);
    });
  },

  endClassNoticeRegister(callBack) {
    const then = signalrNotify;
    if (!then.isEnableSignalrNotice) return;
    then.pushFunctionCallBack(
      then.endClassNoticeRegister,
      callBack,
      "AutoEndClassNotice",
      "endClassNoticeRegister"
    );
    then.hubConnection.on("AutoEndClassNotice", (data) => {
      console.log("autoendclass", data);
      if (typeof callBack === "function" && location.pathname === `/classDetail/${data}`) {
        callBack(data);
      }
    });
  },

  getTutorSysMsgListNotifyRegister(callBack) {
    const then = signalrNotify;
    if (!then.isEnableSignalrNotice) {
      getTutorSysMsg()
        .then((res) => {
          if (res.data?.ErrorCode !== 0) {
            setTimeout(() => then.getTutorSysMsgListNotifyRegister(callBack), then.timer);
            return;
          }
          if (typeof callBack === "function") callBack(res.data.Body);
        })
        .catch(() => {
          setTimeout(() => then.getTutorSysMsgListNotifyRegister(callBack), then.timer);
        });
      return;
    }
    then.pushFunctionCallBack(
      then.getTutorSysMsgListNotifyRegister,
      callBack,
      "GetTutorSysMsg",
      "getTutorSysMsgListNotifyRegister"
    );
    then.hubConnection.on("GetTutorSysMsg", (data) => {
      console.log("GetTutorSysMsg", data);
      if (typeof callBack === "function") callBack(data);
    });
  },

  teacheIsPaypalModifyNotifyRegister(callBack) {
    const then = signalrNotify;
    if (!then.isEnableSignalrNotice) return;
    then.pushFunctionCallBack(
      then.teacheIsPaypalModifyNotifyRegister,
      callBack,
      "TeacheIsPaypalModify",
      "teacheIsPaypalModifyNotifyRegister"
    );
    then.hubConnection.on("TeacheIsPaypalModify", (data) => {
      then.isLocationDebug && console.log("TeacheIsPaypalModify", data);
      if (typeof callBack === "function") callBack(data);
    });
  },

  getNotReadMsgCountNotifyRegister(callBack) {
    const then = signalrNotify;
    if (!then.isEnableSignalrNotice) {
      NotReadMsgCount()
        .then((res) => {
          if (res.data?.ErrorCode !== 0) {
            setTimeout(() => then.getNotReadMsgCountNotifyRegister(callBack), then.timer);
            return;
          }
          if (typeof callBack === "function") callBack(res.data.Body);
        })
        .catch(() => {
          setTimeout(() => then.getNotReadMsgCountNotifyRegister(callBack), then.timer);
        });
      return;
    }
    then.pushFunctionCallBack(
      then.getNotReadMsgCountNotifyRegister,
      callBack,
      "GetTutorNotReadMsgCount",
      "getNotReadMsgCountNotifyRegister"
    );
    then.hubConnection.on("GetTutorNotReadMsgCount", (data) => {
      then.isLocationDebug && console.log("GetTutorNotReadMsgCount", data);
      if (typeof callBack === "function") callBack(data);
    });
  },

  getTutorSelfScheduleRemindRegister(callBack) {
    const then = signalrNotify;
    if (!then.isEnableSignalrNotice) return;
    then.pushFunctionCallBack(
      then.getTutorSelfScheduleRemindRegister,
      callBack,
      "GetTutorSelfScheduleRemind",
      "getTutorSelfScheduleRemindRegister"
    );
    then.hubConnection.on("GetTutorSelfScheduleRemind", (data) => {
      then.isLocationDebug && console.log("GetTutorSelfScheduleRemind", data);
      if (typeof callBack === "function") callBack(data);
    });
  },

  getTutorAttendanceWarning(callBack) {
    const then = signalrNotify;
    if (!then.isEnableSignalrNotice) return;
    then.pushFunctionCallBack(
      then.getTutorAttendanceWarning,
      callBack,
      "AttendanceWarning",
      "getTutorAttendanceWarning"
    );
    then.hubConnection.on("AttendanceWarning", (data) => {
      then.isLocationDebug && console.log("AttendanceWarning", data);
      if (typeof callBack === "function") callBack(data);
    });
  },
};

export default signalrNotify;
